import React from "react";
import { UpcomingMatch } from "../../types/types";
import { useDate } from "../../hooks/useDate";

import { usePlayoffBracket } from "./hooks/usePlayoffBracket";
import { TeamLogo } from "../shared/TeamLogo";

interface PlayOffGraphProps {
  playoffMatches: UpcomingMatch[] | undefined;
  className?: string;
}

const placeholderClass = "text-[#475569] italic text-[0.75rem]";

/** Lower-bracket columns 1–3; upper R1 is col 1, UB final spans cols 2–3. */
const BRACKET_COLUMNS = [1, 2, 3] as const;

export const PlayOffGraph: React.FC<PlayOffGraphProps> = ({ playoffMatches, className }) => {
  const formatDate = useDate();
  const { strings, rounds, getPlaceholder, getWinnerClass } = usePlayoffBracket(playoffMatches);

  if (!rounds) return null;

  const renderMatch = (match: UpcomingMatch, opts: { grandFinal?: boolean } = {}) => {
    const isGrandFinal = opts.grandFinal ?? false;
    const isTeamOneWinner = Boolean(getWinnerClass(match.teamOneScore, match.teamTwoScore));
    const isTeamTwoWinner = Boolean(getWinnerClass(match.teamTwoScore, match.teamOneScore));

    const winnerBorderColor = isGrandFinal ? "#fbbf24" : "#38bdf8";

    const winnerBackground = isGrandFinal
      ? "linear-gradient(90deg, rgba(251, 191, 36, 0.2) 0%, transparent 100%)"
      : "linear-gradient(90deg, rgba(56, 189, 248, 0.15) 0%, transparent 100%)";

    const matchCardClass = [
      "bg-[var(--surface-card-bg)] w-full rounded-[var(--surface-card-radius-sm)]",
      isGrandFinal
        ? "min-w-[14rem] max-w-[20rem] border border-[#fbbf24] shadow-[0_0_20px_rgba(251,191,36,0.1)] max-md:min-w-[15rem]"
        : "min-w-[12rem] border border-[var(--surface-card-border)] max-md:min-w-[13rem]",
    ].join(" ");

    const renderTeam = (
      teamId: string | undefined,
      teamName: string | undefined,
      side: "top" | "bottom",
      isWinner: boolean,
    ) => {
      const borderLeftColor = isWinner ? winnerBorderColor : "transparent";
      const background = isWinner ? winnerBackground : undefined;
      const scoreFont = isWinner ? "font-[800]" : "font-bold";
      const resolvedName =
        teamName?.trim() ||
        teamId?.trim() ||
        getPlaceholder(match.bracket || "UPPER", match.bracketRound || 1, side);
      const hasRealTeam = Boolean(teamName?.trim() || teamId?.trim());
      const logoSize = isGrandFinal ? 28 : 24;

      return (
        <div
          className={[
            "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 transition-[background-color,border-color] duration-200 ease-in-out border-l-[3px] border-l-transparent",
            isGrandFinal ? "p-[12px_14px]" : "p-[10px_12px]",
          ].join(" ")}
          style={{
            borderLeftColor,
            background,
          }}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <TeamLogo
              teamId={teamId?.trim() || strings.COMMON.TBD}
              className={[
                "shrink-0 object-contain rounded-[4px]",
                isGrandFinal ? "h-7 w-7" : "h-6 w-6",
              ].join(" ")}
              alt={resolvedName}
              width={logoSize}
              height={logoSize}
            />
            <span
              className={[
                "min-w-0 leading-snug",
                hasRealTeam ? "truncate" : placeholderClass,
                "max-md:truncate-none max-md:whitespace-normal max-md:break-words",
                isGrandFinal ? "text-[0.9rem]" : "text-[0.8125rem]",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {resolvedName}
            </span>
          </div>

          <span
            className={[
              "justify-self-end tabular-nums text-[#38bdf8] shrink-0 pl-1 text-right",
              scoreFont,
              isGrandFinal ? "text-[1rem]" : "text-[0.875rem]",
            ].join(" ")}
          >
            {side === "top" ? (match.teamOneScore ?? "-") : (match.teamTwoScore ?? "-")}
          </span>
        </div>
      );
    };

    const formattedTime = match.time ? formatDate(match.time) : "";

    return (
      <article
        className={
          isGrandFinal
            ? "relative z-[1] flex w-full max-w-[20rem] flex-col gap-0"
            : "flex flex-col gap-0"
        }
        aria-label={`${match.teamOneName ?? strings.COMMON.TBD} vs ${match.teamTwoName ?? strings.COMMON.TBD}`}
      >
        <time
          className={
            isGrandFinal
              ? "absolute bottom-full left-0 right-0 mb-1 text-center text-[0.65rem] text-[#64748b]"
              : "mb-[4px] min-h-[0.85rem] text-center text-[0.6rem] text-[#64748b]"
          }
          dateTime={match.time || undefined}
        >
          {formattedTime || (isGrandFinal ? null : "\u00a0")}
        </time>

        <div className={matchCardClass}>
          <div style={{ borderBottom: "1px solid #2d2d44" }}>
            {renderTeam(match.teamOne, match.teamOneName, "top", isTeamOneWinner)}
          </div>
          {renderTeam(match.teamTwo, match.teamTwoName, "bottom", isTeamTwoWinner)}
        </div>
      </article>
    );
  };

  return (
    <section
      className={[
        "box-border mx-auto w-full min-w-0 max-w-7xl px-(--padding-view) mt-[clamp(1rem,2vw,2rem)] max-md:mt-[clamp(1.25rem,4vw,2rem)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={strings.PLAYOFFCHART.FINAL}
    >
      <div className="box-border mx-auto w-full min-w-0 min-h-[28rem] overflow-x-auto overscroll-x-contain rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] px-[clamp(0.75rem,1.5vw,1.25rem)] py-[clamp(1.25rem,2vw,1.75rem)] [-webkit-overflow-scrolling:touch] max-md:px-3 max-md:py-5">
        <div
          className="mx-auto grid w-max min-w-[52rem] items-center gap-x-[clamp(0.75rem,2vw,1.75rem)] max-md:min-w-[58rem] max-md:gap-x-3"
          style={{
            gridTemplateColumns: "repeat(3, minmax(12rem, 1fr)) minmax(14rem, 1.15fr)",
            gridTemplateRows: "40px repeat(9, minmax(30px, auto))",
          }}
        >
          {[1, 2, 3].map((r) => (
            <div
              key={r}
              className="text-center uppercase font-extrabold text-[0.75rem] tracking-[2px] text-[#64748b] border-b border-[#2d2d44] pb-2 mb-3 max-md:text-[0.65rem] max-md:tracking-[1px]"
              style={{ gridColumn: r, gridRow: 1 }}
            >
              {`${strings.PLAYOFFCHART.ROUND} ${String(r)}`}
            </div>
          ))}
          <div
            className="text-center uppercase font-extrabold text-[0.75rem] tracking-[2px] text-[#fbbf24] border-b border-[#2d2d44] pb-2 mb-3 [text-shadow:0_0_10px_rgba(251,191,36,0.2)] max-md:text-[0.65rem] max-md:tracking-[1px]"
            style={{ gridColumn: 4, gridRow: 1 }}
          >
            {strings.PLAYOFFCHART.FINALIST}
          </div>

          <div className="self-stretch" style={{ gridColumn: "1 / 4", gridRow: 2 }}>
            <p className="m-0 py-2 text-[0.7rem] font-black tracking-[2px] uppercase text-[#38bdf8]">
              {strings.PLAYOFFCHART.UPPER_BRACKET}
            </p>
          </div>

          {/* Divider + lower label — same type/spacing as upper, text below the line. */}
          <div
            className="self-stretch border-t border-[#2d2d44]"
            style={{ gridColumn: "1 / 4", gridRow: 6 }}
          >
            <p className="m-0 py-2 text-[0.7rem] font-black tracking-[2px] uppercase text-[#fb7185]">
              {strings.PLAYOFFCHART.LOWER_BRACKET}
            </p>
          </div>

          {/* Line connects to championship left edge only; equal spacers keep the card centered. */}
          <div className="relative z-[1] self-stretch" style={{ gridColumn: 4, gridRow: 6 }}>
            <div className="absolute top-0 inset-x-0 flex -translate-y-1/2 items-center px-[clamp(0.25rem,1vw,0.75rem)]">
              <div className="h-px min-w-0 flex-1 bg-[#2d2d44]" aria-hidden />
              <div className="w-[min(100%,20rem)] min-w-[14rem] shrink-0 max-md:min-w-[15rem]">
                {renderMatch(
                  rounds.grandFinal ?? {
                    id: -400,
                    time: "",
                    matchType: "PLAYOFF",
                    link: "",
                    bracketRound: 4,
                  },
                  { grandFinal: true },
                )}
              </div>
              <div className="min-w-0 flex-1" aria-hidden />
            </div>
          </div>

          {/* Upper R1 in col 1; UB final (data R2) centered on the R2/R3 boundary. */}
          <div
            className="flex min-w-0 flex-col justify-center h-full"
            style={{ gridColumn: 1, gridRow: "3/5" }}
          >
            {rounds.upper[0]?.map((m) => (
              <div key={m.id} className="py-2">
                {renderMatch(m)}
              </div>
            ))}
          </div>
          <div className="relative min-w-0 h-full" style={{ gridColumn: "2 / 4", gridRow: "3/5" }}>
            {rounds.upper[1]?.map((m) => (
              <div
                key={m.id}
                className="absolute left-1/2 top-1/2 w-[min(100%,16rem)] -translate-x-1/2 -translate-y-1/2"
              >
                {renderMatch(m)}
              </div>
            ))}
          </div>

          {BRACKET_COLUMNS.map((round) => (
            <div
              key={`lower-${String(round)}`}
              className="flex min-w-0 flex-col justify-center h-full"
              style={{ gridColumn: round, gridRow: "7/10" }}
            >
              {rounds.lower[round - 1]?.map((m) => (
                <div key={m.id} className="py-2">
                  {renderMatch(m)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
